import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';

import { useSelector } from 'react-redux';

import { Typography } from '@material-ui/core';

import { useTranslation, toast } from '@common/utils';

import moment from 'moment';
import { useDetails } from '../../../../ui-common/utils';
import { useSettings } from '@common/hooks';
import http from '@common/http';
import { keyBy, uniq } from 'lodash';

function CheckoutSummary({ booking, pricing }) {
	const classes = useStyles();
	const tr = useTranslation();
	const { locale } = useSelector(state => state.locale);

	const { currencySymbol } = useSettings();

	const { totalBeds, totalFees, totalDeposits, products, totalMandatory, productsGrouped, totalUpfront } = booking;
	const {
		total,
		VATLines,
		discountWithCodeInfo: {
			appliedAmount,
			code,
		},
	} = pricing;

	const totalUpgrades = productsGrouped.filter(p => p.upgrade).reduce((acc, upgrade) => {
		if (upgrade.productUpgradeType === 'recurring') {
			acc += upgrade.price;
		} else {
			acc += upgrade.total;
		}

		return acc;
	}, 0);

	const rooms = booking.productsGrouped.filter(p => p.productAssetType === 'room');

	const [productById, setProductById] = useState({});

	useEffect(() => {

		Promise
			.all(uniq(products, 'product').map(p => http.get(`/api/kiosks/student-housing/products/${p.product}`).catch(() => ({}))))
			.then(responses => responses.map(response => response.data).filter(Boolean))
			.then(products => setProductById(keyBy(products, '_id')))
			.catch(error => {
				console.error(error);
				toast(error);
			});

	}, [productsGrouped]);

	return (
		<>
			<div className={classes.summary}>
				<Typography variant="h3">
					{tr('Booking summary')}
				</Typography>
				{rooms.map((p, i) => {

					const { details: product } = useDetails(`/api/kiosks/student-housing/products/${p.product}`);

					if (!product) {
						return;
					}

					const isDorm = p.productAssetType === 'dorm';

					return (
						<div key={`product-${i}`}>
							<div className={classes.product}>
								<Typography variant="h4" className={classes.headline}>
									{product?.i18n[locale]?.title || product?.title || '-'}
								</Typography>
								{p.from && p.to && <div className={classes.dates}>
									<div>
										<b>{tr('Check-in')}</b>
										&nbsp;
										{moment(p.from).format('MMM DD, YYYY')}
									</div>
									<div>
										<b>{tr('Check-Out')}</b>
										&nbsp;
										{moment(p.to).format('MMM DD, YYYY')}
									</div>
								</div>}
							</div>


							<div className={classes.rows}>
								<div className={classes.row}>
									<div>1 {tr(isDorm ? 'bed' : 'room')}</div>
									<div>{p.price} {currencySymbol}</div>
								</div>

								{p.quantityMonths > 0
									? <div className={`${classes.row} ${classes.rowQTY}`}>
										<div>{tr('Quantity')}</div>
										<div>{p.quantityMonths} {tr('months')}</div>
									</div>
									: <div className={`${classes.row} ${classes.rowQTY}`}>
										<div>{tr('Quantity')}</div>
										<div>{p.quantity}</div>
									</div>
								}
							</div>
						</div>
					);
				})}
				<div className={classes.rows}>

					{/* {totalRooms > 0 &&
						<>
							<div className={classes.row}>
								<div>{tr('Accomodation')}</div>
								<div>{totalRooms} {currencySymbol}</div>
							</div>
							<div className={classes.separator} />
						</>
					} */}

					{totalBeds > 0 &&
						<>
							<div className={classes.row}>
								<div>{tr('Beds')}</div>
								<div>{totalBeds} {currencySymbol}</div>
							</div>
							<div className={classes.separator} />
						</>
					}

					{totalFees > 0 &&
						<>
							<div className={classes.row}>
								<div>{tr('Fees')}</div>
								<div>{totalFees} {currencySymbol}</div>
							</div>
							<div className={classes.separator} />
						</>
					}

					{((totalMandatory > 0 && (totalUpfront - totalMandatory !== totalDeposits)) || totalUpgrades > 0) && <div className={classes.row} style={{ paddingBottom: '4px' }}>
						<div>{tr('Extras')}</div>
					</div>}
					{productsGrouped.length > 0 && (productsGrouped || []).map((p) => {
						return (
							<>
								{p.mandatoryBookingProducts.filter(p => p.productMandatoryType !== 'onetime-pay-upfront').map(p => {
									const vatPercent = VATLines.find(v => v.products.includes(String(p.product)))?.percent;
									const isRecurring = productById[p.product]?.mandatoryType === 'recurring';
									return (
										<div key={p._id} style={{ paddingBottom: '12px' }}>
											<div className={classes.subRow}>
												<div>&nbsp;&nbsp;&nbsp;&nbsp;{productById[p.product]?.i18n[locale]?.title || productById[p.product]?.title}</div>
												<div>{(p.total - (p.total - (p.total / (1 + vatPercent / 100)))).toFixed(2)}{currencySymbol}/{isRecurring ? tr('month') : tr('unit')}</div>
											</div>
											<div className={classes.subRow}>
												<div>
													&nbsp;&nbsp;&nbsp;&nbsp;{`${tr('VAT')} (${vatPercent || 0}%)`}
												</div>
												<div>
													{(p.total - (p.total / (1 + vatPercent / 100))).toFixed(2)}{currencySymbol}
												</div>
											</div>
										</div>
									);
								})}
							</>
						);
					})}
					{productsGrouped.filter(i => i.upgrade).map((p) => {
						const vatPercent = VATLines.find(v => v.products.includes(String(p.product)))?.percent;
						const isRecurring = productById[p.product]?.upgradeType === 'recurring';
						const total = isRecurring ? p.price : p.total;
						return (
							p.upgrade === true && !!productById[p.product] &&
							<div key={p._id} style={{ paddingBottom: '12px' }}>
								<div className={classes.subRow}>
									<div>&nbsp;&nbsp;&nbsp;&nbsp;{productById[p.product]?.i18n[locale]?.title || productById[p.product]?.title}</div>
									<div>{(total - (total - (total / (1 + vatPercent / 100)))).toFixed(2)}{currencySymbol}/{isRecurring ? tr('month') : tr('unit')}</div>
								</div>
								<div className={classes.subRow}>
									<div>
										&nbsp;&nbsp;&nbsp;&nbsp;{`${tr('VAT')} (${vatPercent || 0}%)`}
									</div>
									<div>
										{(total - (total / (1 + vatPercent / 100))).toFixed(2)}{currencySymbol}
									</div>
								</div>
							</div>
						);
					})
					}

					<div className={classes.separator} />

					{/* {totalVAT >= 0 && VATLines.map(vat => <div key={vat.percent} className={`${classes.row}`}>
						<div>
							{`${tr('VAT')} (${vat.percent}%)`}
						</div>
						<div>
							{vat.amount.toFixed(2)} {currencySymbol}
						</div>
					</div>)} */}
					{code && appliedAmount >= 0 && <div className={`${classes.row} ${classes.total}`}>
						<div>
							{`${tr('Discount')} (${code})`}
						</div>
						<div>
							&minus;{appliedAmount} {currencySymbol}
						</div>
					</div>}

					{(!totalUpfront || totalUpfront === 0) && <div className={`${classes.row} ${classes.total}`}>
						<Typography variant="h5">
							{tr('Total (taxes incl.)')}
						</Typography>
						<Typography variant="h5">
							{total} {currencySymbol}
						</Typography>
					</div>}

					{(totalDeposits > 0 || totalUpfront > 0) && <>
						<div className={`${classes.deposit} ${classes.total}`}>
							<Typography variant="h5">
								{tr('Due today')}
							</Typography>
							<Typography variant="h5">
								{total} {currencySymbol}
							</Typography>
						</div>
						{totalDeposits > 0 && <div className={`${classes.row} ${classes.indentedRow}`}>
							<div>
								{tr('Deposit')}
							</div>
							<div>
								{totalDeposits} {currencySymbol}
							</div>
						</div>}
						{products?.length > 0 && products.filter(p => p.productMandatoryType === 'onetime-pay-upfront').map((p) => {
							const vatPercent = VATLines.find(v => v.products.includes(String(p.product)))?.percent;

							return (<div key={p._id} style={{ paddingBottom: '12px' }} className={`${classes.indentedRow}`}>
								<div className={classes.subRow}>
									<div>{productById[p.product]?.i18n[locale]?.title || productById[p.product]?.title}</div>
									{p.mandatoryProductType !== 'onetime-pay-upfront' && <div>{(p.total - (p.total - (p.total / (1 + vatPercent / 100)))).toFixed(2)}{currencySymbol}</div>}
								</div>
								<div className={classes.subRow}>
									<div>
										{`${tr('VAT')} (${vatPercent || 0}%)`}
									</div>
									<div>
										{(p.total - (p.total / (1 + vatPercent / 100))).toFixed(2)}{currencySymbol}
									</div>
								</div>
							</div>);
						})}
					</>}
				</div>
			</div>
		</>
	);
}

CheckoutSummary.propTypes = {
	booking: PropTypes.object.isRequired,
	pricing: PropTypes.object.isRequired,
};

export default CheckoutSummary;

const useStyles = makeStyles(theme => ({
	summary: {
		marginBottom: theme.spacing(5),
		padding: `${theme.spacing(4)}px ${theme.spacing(3)}px 0`,
		borderRadius: '4px',
		backgroundColor: theme.palette.custom.background,
	},

	upgrades: {
		marginBottom: theme.spacing(5),
	},

	rows: {
		padding: `${theme.spacing(1)}px 0 ${theme.spacing(1)}px`,
	},
	row: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		padding: `${theme.spacing(2)}px 0`,
	},
	subRow: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		fontSize: 'small',
	},
	rowQTY: {
		paddingTop: 0,
	},
	total: {
		fontWeight: 300,
	},
	separator: {
		height: '1px',
		backgroundColor: theme.palette.custom.separator,
	},

	headline: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	product: {
		// marginBottom: theme.spacing(3),
	},

	section: {
		paddingLeft: 0,
		fontSize: '18px',
		color: theme.palette.primary.light,
		fontWeight: 600,
	},
	col: {
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
		borderBottom: 0,
	},
	first: {
		paddingLeft: 0,
	},
	last: {
		paddingRight: 0,
	},
	sep: {
		paddingLeft: 0,
		paddingRight: 0,
	},
	separator2: {
		height: '1px',
		backgroundColor: theme.palette.custom.separator,
	},
	span: {
		fontSize: '14px',
	},
	deposit: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		padding: `${theme.spacing(2)}px 0 0 0`,
	},
	indentedRow: {
		textIndent: '8px',
	},
}));
